import { Component, computed, OnInit, Signal, signal, inject } from '@angular/core';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import {
    FieldFieldComponent,
    FieldMappingComponent,
    FieldRowComponent,
    FormRowComponent,
    InstructionsComponent,
    InstructionsWithFieldsContainerComponent,
    TitledContainerComponent,
} from '@r1-compass-library/components';
import { FormArray, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IamDivision, IamDivisionSalesforceUserMap, IamRole, IamUser } from '@r1-compass-library/models';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';

import { DivisionSalesforceUserForm } from './user-settings-form.model';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';

export type UserSettingsDialogData = {
    user: IamUser | undefined;
    divisions: IamDivision[];
    roles: IamRole[];
    mode: 'add' | 'edit';
    currentDivisionId: number;
};

export type UserSettingsDialogReturnData = {
    username: string;
    password: string | undefined;
    firstName: string;
    lastName: string;
    email: string;
    iamDivisions: number[];
    iamRole: number;
    divisionSalesforceUserMap: IamDivisionSalesforceUserMap[];
};

@Component({
    selector: 'r1-compass-app-user-settings',
    imports: [
        InstructionsWithFieldsContainerComponent,
        InstructionsComponent,
        FormRowComponent,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButton,
        MatSelectModule,
        MatMenuModule,
        TitledContainerComponent,
        MatIcon,
        MatMiniFabButton,
        MatTooltip,
        FieldFieldComponent,
        FieldMappingComponent,
        FieldRowComponent,
        FontAwesomeModule,
    ],
    templateUrl: './user-settings.component.html',
    styles: `
        #footer {
            display: flex;
            justify-content: flex-end;
        }
        #password-row {
            display: flex;
            align-items: center;
            gap: 8px;

            button {
                margin-bottom: 21.5px;
            }
            mat-form-field {
                width: 100%;
            }
        }
        .add-division-container {
            margin-bottom: 20px;
        }
    `,
})
export class UserSettingsComponent implements OnInit {
    readonly #fb = inject(NonNullableFormBuilder);
    protected readonly loggedInUserStore = inject(GlobalLoggedInUserStore);
    protected dialogRef = inject<MatDialogRef<UserSettingsComponent>>(MatDialogRef);
    protected data = inject<UserSettingsDialogData>(MAT_DIALOG_DATA);
    readonly initialDivisionSalesforceUserMap: IamDivisionSalesforceUserMap[] = (
        this.data.user?.divisionSalesforceUserMap ?? []
    ).map(mapping => ({
        ...mapping,
    })); // This is a clone of the mapping array, so we aren't mutating the original data

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faTrashAlt);
    }

    faTrashAlt = faTrashAlt;

    showPasswordLock = signal(false);

    divisionSalesforceUserMap = signal(this.initialDivisionSalesforceUserMap);

    divisions: Signal<IamDivision[]> = signal(
        this.data.divisions.sort(function (a, b) {
            return a.divisionCode.localeCompare(b.divisionCode);
        }) ?? []
    );

    claimed = computed(() =>
        this.divisionSalesforceUserMap().map(map => {
            return this.divisions().find(division => division.id === map.divisionId);
        })
    );

    claimsMapped = computed(() => {
        const claimed = this.claimed();

        function mapClaimData(divisions: IamDivision[]) {
            return divisions.map((division, index) => {
                if (claimed.includes(division)) {
                    return {
                        division: division,
                        claimed: true,
                        index: index,
                    };
                } else {
                    return {
                        division: division,
                        claimed: false,
                        index: index,
                    };
                }
            });
        }

        return mapClaimData(this.divisions());
    });

    userInfoForm = this.#fb.group({
        username: this.#fb.control<string>('', [Validators.required]),
        firstName: this.#fb.control<string>('', [Validators.required]),
        lastName: this.#fb.control<string>('', [Validators.required]),
        email: this.#fb.control<string>('', [Validators.required, Validators.email]),
        password: this.#fb.control<string | undefined>(undefined, [Validators.required]),
        iamDivisions: this.#fb.control<number[]>([]),
        iamRole: this.#fb.control<number | undefined>(undefined, [Validators.required]),
        divisionSalesforceUserMap: this.#fb.array<FormGroup<DivisionSalesforceUserForm>>([]),
    });

    divisionSalesforceUserFormArray: FormArray<FormGroup<DivisionSalesforceUserForm>> =
        this.userInfoForm.controls.divisionSalesforceUserMap;

    ngOnInit() {
        this.initForm();
        if (this.data.mode === 'edit') {
            this.userInfoForm.controls.password.disable();
            this.showPasswordLock.set(true);
        } else {
            this.showPasswordLock.set(false);
        }
    }

    enablePasswordEditing() {
        this.userInfoForm.controls.password.reset();
        this.userInfoForm.controls.password.enable();
    }

    addSalesforceUserRow() {
        this.divisionSalesforceUserFormArray.push(this.buildDivisionUserFormGroup(undefined, undefined));
    }

    deleteSalesforceUserRow(index: number) {
        const existingSelectedDivisionSalesforceUserRow = this.divisionSalesforceUserFormArray.at(index);
        const existingDivisionId = existingSelectedDivisionSalesforceUserRow.controls.divisionId.value;
        if (existingDivisionId) {
            const divisions = this.userInfoForm.controls.iamDivisions.value;
            const existingDivisionIndex = divisions.indexOf(existingDivisionId);
            if (existingDivisionIndex > -1) {
                this.userInfoForm.controls.iamDivisions.value.splice(existingDivisionIndex, 1);
            }
            const existingMapIndex = this.divisionSalesforceUserMap().findIndex(
                map => map.divisionId === existingDivisionId
            );
            if (existingMapIndex > -1) {
                this.divisionSalesforceUserMap.update(map => {
                    map.splice(existingMapIndex, 1);
                    return [...map];
                });
            }
        }

        this.divisionSalesforceUserFormArray.removeAt(index);
        this.divisionSalesforceUserFormArray.markAllAsDirty();
        this.userInfoForm.markAllAsDirty();
    }

    updateSelectedDivision(event: MatOptionSelectionChange, rowIndex: number) {
        const newDivisionId = event.source.value;
        const existingSelectedDivisionSalesforceUserRow = this.divisionSalesforceUserFormArray.at(rowIndex);
        const existingDivisionId = existingSelectedDivisionSalesforceUserRow.controls.divisionId.value;
        if (existingDivisionId) {
            if (existingDivisionId !== newDivisionId) {
                const divisions = this.userInfoForm.controls.iamDivisions.value;
                const divisionIndex = divisions.indexOf(existingDivisionId);
                if (divisionIndex > -1) {
                    this.userInfoForm.controls.iamDivisions.value.splice(divisionIndex, 1);
                }
                const mapIndex = this.divisionSalesforceUserMap().findIndex(
                    map => map.divisionId === existingDivisionId
                );
                if (mapIndex > -1) {
                    this.divisionSalesforceUserMap.update(map => {
                        const division: IamDivisionSalesforceUserMap = map.at(mapIndex)!;
                        division.divisionId = newDivisionId;
                        return [...map];
                    });
                }
                this.userInfoForm.controls.iamDivisions.value.push(newDivisionId);
            }
        } else {
            this.userInfoForm.controls.iamDivisions.value.push(newDivisionId);
        }
    }

    private initForm() {
        const patchData = {
            username: this.data?.user?.username ?? '',
            firstName: this.data?.user?.firstName ?? '',
            lastName: this.data?.user?.lastName ?? '',
            email: this.data?.user?.email ?? '',
            password: undefined,
            iamDivisions: this.data?.user?.iamUserDivisionMaps?.map(div => div.divisionId) ?? [],
            iamRole: this.data.user?.iamUserDivisionMaps
                .flatMap(m => m.iamUserRoleDivMaps)
                .map(m => m.iamRole)
                .find(role => !!role)!.id, // get the first non-null role, it'll be the same for all divisions
            divisionSalesforceUserMap: this.initialDivisionSalesforceUserMap,
        };

        patchData.divisionSalesforceUserMap.forEach(divisionSalesforceUserMap => {
            this.divisionSalesforceUserFormArray.push(
                this.buildDivisionUserFormGroup(
                    divisionSalesforceUserMap.divisionId,
                    divisionSalesforceUserMap.salesforceUserId
                )
            );
        });
        // The division/salesforce user array must have at least one entry in it to begin.
        if (this.divisionSalesforceUserFormArray.length < 1) {
            this.divisionSalesforceUserFormArray.push(this.buildDivisionUserFormGroup(undefined, undefined));
        }
        this.userInfoForm.patchValue(patchData);
    }

    private buildDivisionUserFormGroup(divisionId: number | undefined, salesforceUserId: string | undefined) {
        return this.#fb.group({
            divisionId: this.#fb.control(divisionId, Validators.required),
            salesforceUserId: this.#fb.control(salesforceUserId, Validators.required),
        });
    }
}
